/**
 * The mobile fallback, in the design canvas's own numbers.
 *
 * As on desktop, every value is the literal pixel the canvas source sets, divided by the
 * artboard width and spent as a fraction of the stage. The phone side needs no detective
 * work for that width: the canvas declares the handset at 390 and scales its own brand
 * marks by `width / 390`, so the artboard width is stated rather than inferred.
 *
 * Fractions rather than pixels for the same reason as desktop — a fixed `p-5` is the same
 * twenty pixels on every handset, reading as designed on the phone it was picked on and
 * growing visually tighter as the screen widens, which is the drift that put the lower half
 * out of proportion with the artboard.
 *
 * Consumers read these through the `--stage-w` custom property, which the mobile screen
 * sets once on its root, so nested components scale without being handed a width.
 */

/** The width the canvas artboard declares for the handset. */
export const ARTBOARD_WIDTH = 390;

/**
 * Capped so the composition does not keep growing on a tablet held in portrait, where
 * phone proportions would otherwise produce oversized type.
 */
export const MOBILE_STAGE_WIDTH = 'min(100vw, 480px)';

/** Literal canvas pixels. Spend them through `mobilePx`, never directly. */
export const MOBILE_PX = {
  poweredBy: 28,
  headline: 22,
  subcopy: 14,
  fieldLabel: 12,
  fieldText: 14,
  submit: 14,
  /** Confirmation after the email goes out — a step above the submit label it replaces. */
  sentConfirm: 16,
  quietAction: 11,
  fineNote: 11,

  brandGap: 12,
  headlineGap: 8,
  copyBlockGap: 18,
  cardPadding: 16,
  cardRadius: 14,
  labelGap: 6,
  fieldGap: 12,
  submitPaddingY: 12,
  submitRadius: 10,
  copyLinkGap: 14,
  copyLinkRowGap: 8,
  poweredByGap: 16,

  /**
   * The field's own three. The canvas builds it from a host input rather than styling one,
   * so unlike everything above these are measured off the artboard. The review found the
   * field standing a third too tall, and its height is padding plus leading.
   */
  fieldPaddingY: 5.5,
  fieldPaddingX: 12,
  fieldRadius: 7,
} as const;

/** A share of the stage width, for the values the dashboard states as fractions. */
export const mobileScaled = (fraction: number) => `calc(var(--stage-w) * ${fraction})`;

/** A canvas pixel, as a share of the stage. */
export const mobilePx = (canvasPx: number) => mobileScaled(canvasPx / ARTBOARD_WIDTH);

/**
 * The card's fill and edge.
 *
 * Translucent rather than solid: the wash has thinned the campaign to almost nothing by
 * this point, and letting the last of it through is what keeps the card sitting *in* the
 * screen instead of on top of it. The edge is a mid purple, quieter than the QR rim, so
 * the card frames the form without competing with the primary action inside it.
 */
export const CARD_FILL_ALPHA = 0.78;
export const CARD_EDGE_COLOR = '#5B2D8C';

/** A blur radius in canvas pixels, as a share of the stage. See the desktop equivalent. */
export const mobileBlur = mobilePx;
